// @ts-nocheck
import { checkboxSizes } from "@insite/mobius/Checkbox";
import { RadioStyle } from "@insite/mobius/Radio";
import RadioGroupContext from "@insite/mobius/RadioGroup/RadioGroupContext";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import * as React from "react";
import styled, { css, withTheme } from "styled-components";

/* Starter Custom Code - Start */
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { RequiredWrapper } from "@store/MobiusOverrides/FormField/FormField";
/* Starter Custom Code - End */

export interface RadioGroupPresentationProps {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<RadioGroupProps>;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /**
     * Controls the alignment of the Radio children.
     * Defaults to vertical alignment.
     */
    horizontal?: boolean;
}

export type RadioGroupComponentProps = MobiusStyledComponentProps<
    "fieldset",
    {
        /** Indicates an error by changing the color of the radiogroup label. */
        error?: React.ReactNode;
        /** Label to be displayed above the radio group. */
        label?: React.ReactNode;
        /** Handler for the change event shared by the radio inputs within this radio group. */
        onChangeHandler?: React.ChangeEventHandler<HTMLInputElement>;
        /** Sets the value of the RadioGroup. */
        value?: string;
        required?: boolean;
        name?: string;
        // Starter Custom Code
        /** Label id that will be used for aria-labelledby for accessibility purposes. */
        labelId?: string;
    }
>;

export type RadioGroupProps = FieldSetGroupPresentationProps<RadioGroupComponentProps> &
    RadioGroupComponentProps &
    RadioGroupPresentationProps;

/* Starter Custom Code - Start */
export type RadioGroupStyleProps = {
    _sizeVariant: keyof typeof checkboxSizes;
    horizontal?: boolean;
};
/* Starter Custom Code - End */

/* Starter Custom Code - next line - RadioGroupStyleProps */
const RadioGroupStyle = styled.fieldset<RadioGroupStyleProps>`
    display: block; /* Starter Custom Code */
    border: 0;
    padding: 0;
    margin: 0;

    & > ${TypographyStyle as any} {
        padding: 0;
        /* Starter Custom Code - Start */
        display: block;
        &:first-child {
            margin-bottom: 10px;
        }
        &:last-child {
            margin-top: 10px;
        }
        /* Starter Custom Code - End */
    }
    ${props =>
        props.horizontal
            ? css`
                  /* Starter Removed - display */
                  & > ${RadioStyle} {
                      display: inline-block;
                      vertical-align: top; /* Starter Custom Code */
                      margin-top: 5px;
                      margin-right: 20px;
                  }
              `
            : css`
                  /* Starter Removed - display */
                  /* Starter Removed - flex-direction */
                  ${RadioStyle} + ${RadioStyle} {
                      margin-top: 10px;
                  }
              `}
    ${injectCss}
`;

type State = {
    value?: string;
    name?: string;
};

class RadioGroup extends React.Component<RadioGroupProps, State> {
    state: State = {
        value: this.props.value,
    };

    static getDerivedStateFromProps(nextProps: RadioGroupProps, prevState: State) {
        const nextState: State = {
            name: nextProps.name || (prevState && prevState.name) || uniqueId(),
        };
        if (nextProps.value !== prevState.value) {
            nextState.value = nextProps.value;
        }
        return nextState;
    }

    handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ value: e.target.value });
        this.props.onChangeHandler?.(e);
    };

    render() {
        const { children, error, label, mergeCss, required, horizontal, ...otherProps } = this.props;
        const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(this.props, {
            component: "radio",
            category: "fieldSet",
            propKey: "groupDefaultProps",
        });
        const sizeVariant = applyProp("sizeVariant", "default") as keyof typeof checkboxSizes;

        const resolvedMergeCss = mergeCss ?? this.props?.theme?.radio?.groupDefaultProps?.mergeCss;

        return (
            <RadioGroupContext.Provider
                value={{
                    name: this.state.name,
                    value: this.state.value,
                    sizeVariant,
                    onChange: this.handleChange,
                }}
            >
                <RadioGroupStyle
                    {...omitMultiple(otherProps, ["sizeVariant", "onChangeHandler"])}
                    _sizeVariant={otherProps.sizeVariant || "default" /* Starter Custom Code */}
                    horizontal={horizontal}
                    css={applyStyledProp("css", resolvedMergeCss)}
                    aria-labelledby={this.props.labelId} // Starter Custom Code
                >
                    {label /* Starter Custom Code */ ? (
                        <Typography
                            as="legend"
                            weight={600}
                            size={sizeVariantValues[sizeVariant]?.fontSize || checkboxSizes[sizeVariant].fontSize} // Starter Custom Code
                            {...spreadProps("labelProps")}
                        >
                            {label}
                            {required && <RequiredWrapper /> /* Starter Custom Code */}
                        </Typography>
                    ) : /* Starter Custom Code */ null}
                    {children}
                    {error /* Starter Custom Code */ ? (
                        <Typography
                            color="danger"
                            /* Store Custom Code - Start */
                            weight={400}
                            size={
                                (sizeVariantValues[sizeVariant] as any)?.descriptionFontSize ||
                                checkboxSizes[sizeVariant].fontSize
                            }
                            /* Store Custom Code - End */
                            {...spreadProps("errorProps")}
                        >
                            {error}
                        </Typography>
                    ) : /* Starter Custom Code */ null}
                </RadioGroupStyle>
            </RadioGroupContext.Provider>
        );
    }
}

/** @component */
export default withTheme(RadioGroup);

export { RadioGroupStyle };
